import { Injectable } from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';

import { Observable, catchError, of, throwError } from 'rxjs';

import { NeptuneApi } from '../genericApi/neptune.api';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import {
    AccountQuotaUsageQuery,
    AgentAdhocSourceResponse,
    CreateCurationAgentMentionRequest,
    CreateCurationAgentRequest,
    CreateCurationAgentSourceRequest,
    CurationAgentListItemResponse,
    CurationAgentResponse,
    CurationAgentSourceResponse,
    CuratorQuotaResponse,
    emptyCuratorQuota,
    NotificationPreferenceResponse,
    SetNotificationPreferenceRequest,
    UpdateCurationAgentRequest,
    UpdateCurationAgentSourceStatusRequest
} from './models/curation-agent.api';

/** What agents-service answers with when an account has never had a quota pool configured */
const notFoundStatus: number = 404;

/**
 * Curator agent endpoints - agents-service `api/v1/agents/curator`, under the Neptune api url.
 * Provided in root so the source pickers and the monitoring sidebar outside the Automation Agents
 * module can read agents too.
 */
@Injectable({ providedIn: 'root' })
export class CurationAgentsApiService {

    static readonly agentsSlug: string = '/agents/curator';
    static readonly quotaSlug: string = '/agents/curator/quota';
    static readonly notificationPreferencesSlug: string = '/agents/curator/notification-preferences';

    constructor(
        private neptuneApi: NeptuneApi
    ) {}

    static agentSlug(agentId: number): string {
        return `${CurationAgentsApiService.agentsSlug}/${agentId}`;
    }

    static sourcesSlug(agentId: number): string {
        return `${CurationAgentsApiService.agentSlug(agentId)}/sources`;
    }

    static sourceSlug(agentId: number, sourceId: number): string {
        return `${CurationAgentsApiService.sourcesSlug(agentId)}/${sourceId}`;
    }

    // -----------------------------------------------------------------------
    // Agents
    // -----------------------------------------------------------------------

    /** The list shape carries each agent's sources, but not its actions - see {@link get} for those */
    list(): Observable<CurationAgentListItemResponse[]> {
        return this.neptuneApi.api<CurationAgentListItemResponse[]>(
            CurationAgentsApiService.agentsSlug,
            null,
            HttpVerbsEnum.GET
        );
    }

    get(agentId: number): Observable<CurationAgentResponse> {
        return this.neptuneApi.api<CurationAgentResponse>(
            CurationAgentsApiService.agentSlug(agentId),
            null,
            HttpVerbsEnum.GET
        );
    }

    create(request: CreateCurationAgentRequest): Observable<CurationAgentResponse> {
        return this.neptuneApi.api<CurationAgentResponse>(
            CurationAgentsApiService.agentsSlug,
            request,
            HttpVerbsEnum.POST
        );
    }

    /** An omitted action list is left alone; an empty one clears that trigger */
    update(agentId: number, request: UpdateCurationAgentRequest): Observable<CurationAgentResponse> {
        return this.neptuneApi.api<CurationAgentResponse>(
            CurationAgentsApiService.agentSlug(agentId),
            request,
            HttpVerbsEnum.PATCH
        );
    }

    delete(agentId: number): Observable<null> {
        return this.neptuneApi.api<null>(
            CurationAgentsApiService.agentSlug(agentId),
            null,
            HttpVerbsEnum.DELETE
        );
    }

    pause(agentId: number): Observable<CurationAgentResponse> {
        return this.neptuneApi.api<CurationAgentResponse>(
            `${CurationAgentsApiService.agentSlug(agentId)}/pause`,
            null,
            HttpVerbsEnum.POST
        );
    }

    resume(agentId: number): Observable<CurationAgentResponse> {
        return this.neptuneApi.api<CurationAgentResponse>(
            `${CurationAgentsApiService.agentSlug(agentId)}/resume`,
            null,
            HttpVerbsEnum.POST
        );
    }

    /** Resolves the submission into a run, which is returned so it can be listed straight away */
    submitMentions(
        agentId: number,
        request: CreateCurationAgentMentionRequest
    ): Observable<AgentAdhocSourceResponse> {
        return this.neptuneApi.api<AgentAdhocSourceResponse>(
            `${CurationAgentsApiService.agentSlug(agentId)}/mentions`,
            request,
            HttpVerbsEnum.POST
        );
    }

    // -----------------------------------------------------------------------
    // Sources
    // -----------------------------------------------------------------------

    listSources(agentId: number): Observable<CurationAgentSourceResponse[]> {
        return this.neptuneApi.api<CurationAgentSourceResponse[]>(
            CurationAgentsApiService.sourcesSlug(agentId),
            null,
            HttpVerbsEnum.GET
        );
    }

    createSource(agentId: number, request: CreateCurationAgentSourceRequest): Observable<CurationAgentSourceResponse> {
        return this.neptuneApi.api<CurationAgentSourceResponse>(
            CurationAgentsApiService.sourcesSlug(agentId),
            request,
            HttpVerbsEnum.POST
        );
    }

    updateSourceStatus(
        agentId: number,
        sourceId: number,
        request: UpdateCurationAgentSourceStatusRequest
    ): Observable<CurationAgentSourceResponse> {
        return this.neptuneApi.api<CurationAgentSourceResponse>(
            CurationAgentsApiService.sourceSlug(agentId, sourceId),
            request,
            HttpVerbsEnum.PATCH
        );
    }

    deleteSource(agentId: number, sourceId: number): Observable<null> {
        return this.neptuneApi.api<null>(
            CurationAgentsApiService.sourceSlug(agentId, sourceId),
            null,
            HttpVerbsEnum.DELETE
        );
    }

    // -----------------------------------------------------------------------
    // Ad hoc runs
    // -----------------------------------------------------------------------

    /** Every ad hoc run for the account, across all of its agents, newest first */
    listAccountAdhocRuns(): Observable<AgentAdhocSourceResponse[]> {
        return this.neptuneApi.api<AgentAdhocSourceResponse[]>(
            `${CurationAgentsApiService.agentsSlug}/adhoc`,
            null,
            HttpVerbsEnum.GET
        );
    }

    /** One agent's ad hoc runs, newest first */
    listAdhocRuns(agentId: number): Observable<AgentAdhocSourceResponse[]> {
        return this.neptuneApi.api<AgentAdhocSourceResponse[]>(
            `${CurationAgentsApiService.agentSlug(agentId)}/adhoc`,
            null,
            HttpVerbsEnum.GET
        );
    }

    getAdhocRun(agentId: number, adhocId: number): Observable<AgentAdhocSourceResponse> {
        return this.neptuneApi.api<AgentAdhocSourceResponse>(
            `${CurationAgentsApiService.agentSlug(agentId)}/adhoc/${adhocId}`,
            null,
            HttpVerbsEnum.GET
        );
    }

    // -----------------------------------------------------------------------
    // Notification preferences
    // -----------------------------------------------------------------------

    /** The caller's own preference. Defaults to enabled for a user who has never set one */
    getNotificationPreference(): Observable<NotificationPreferenceResponse> {
        return this.neptuneApi.api<NotificationPreferenceResponse>(
            CurationAgentsApiService.notificationPreferencesSlug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /** PUT rather than POST: it upserts, where POST rejects a preference that is already set */
    setNotificationPreference(enabled: boolean): Observable<NotificationPreferenceResponse> {
        return this.neptuneApi.api<NotificationPreferenceResponse>(
            CurationAgentsApiService.notificationPreferencesSlug,
            { enabled: enabled } as SetNotificationPreferenceRequest,
            HttpVerbsEnum.PUT
        );
    }

    // -----------------------------------------------------------------------
    // Quota
    // -----------------------------------------------------------------------

    /**
     * Account-wide pool, shared across every agent type rather than just curation.
     *
     * An account that has never had a pool configured 404s here. That is a limit of zero rather
     * than a failure, so it resolves to an empty pool and callers have one shape to read.
     */
    getQuota(query: AccountQuotaUsageQuery = {}): Observable<CuratorQuotaResponse> {
        return this.neptuneApi.api<CuratorQuotaResponse>(
            CurationAgentsApiService.quotaSlug,
            null,
            HttpVerbsEnum.GET,
            query
        ).pipe(
            catchError((error: HttpErrorResponse) => error?.status === notFoundStatus
                ? of(emptyCuratorQuota())
                : throwError(() => error))
        );
    }
}
